import React, { useState } from "react";
import { fetchAssignmentResponses } from "../../services/assignmentResponseService/assignmentResponseService";
import {
  AssignmentResponsesPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ResponseStatusCell,
  ResponseActionCell,
} from "./AssignmentResponsesPage.styles";
import Sidebar from "../../components/Sidebar";
import { AssignmentResponseRow } from "./AssignmentResponsesPage.types";
import Table, { TableLink, TableText } from "../../components/Table";
import TableMetrics from "../../components/TableMetrics";
import StatusBadge from "../../components/StatusBadge";
import LessonsButton from "../../components/Buttons/LessonsButton";

import { useListPage } from "hooks/useListPage";

const AssignmentResponsesPage: React.FC = () => {
  const [unreviewedCount, setUnreviewedCount] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [filterOptions, setFilterOptions] = useState({
    courses: [] as Array<{ label: string; value: string }>,
    lessons: [] as Array<{ label: string; value: string }>,
    assignments: [] as Array<{ label: string; value: string }>,
    students: [] as Array<{ label: string; value: string }>,
  });
  const filterKey = JSON.stringify(filters);

  const {
    items: assignmentResponses,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
  } = useListPage<
    AssignmentResponseRow,
    Awaited<ReturnType<typeof fetchAssignmentResponses>>
  >({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchAssignmentResponses(page, size, orderBy, {
        courseIds: filters.course ?? [],
        lessonIds: filters.lesson ?? [],
        assignmentIds: filters.assignment ?? [],
        studentIds: filters.studentName ?? [],
      }),
    select: (response) => ({
      items: response.results.assignment_responses.map((response) => ({
        id: response.id,
        studentName: (
          <TableText weight={500}>{response.student.full_name}</TableText>
        ),
        status: (
          <ResponseStatusCell>
            <StatusBadge
              text={response.is_reviewed ? "Проверено" : "Не проверено"}
              variant={response.is_reviewed ? "green" : "red"}
              appearance="soft"
            />
          </ResponseStatusCell>
        ),
        assignment: <TableText>{response.assignment.title}</TableText>,
        course: <TableText>{response.course.title}</TableText>,
        lesson: <TableText>{response.lesson.title}</TableText>,
        assignmentTitle: (
          <ResponseActionCell>
            <LessonsButton
              href={`/assignment-responses/${response.id}`}
              text="Смотреть"
            />
          </ResponseActionCell>
        ),
        telegram: (
          <TableLink
            href={`https://t.me/${response.student.user_name}`}
            external
          >
            @{response.student.user_name}
          </TableLink>
        ),
        phoneNumber: (
          <TableText tone="muted" numeric>
            {response.student.phone_number}
          </TableText>
        ),
      })),
      total: response.count,
    }),
    onLoaded: (response) => {
      setUnreviewedCount(response.results.unreviewed_count);
      const options = response.results.filter_options;
      setFilterOptions({
        courses: options?.courses ?? [],
        lessons: options?.lessons ?? [],
        assignments: options?.assignments ?? [],
        students: options?.students ?? [],
      });
    },
    errorMessage: "Ошибка загрузки ответов на задания",
    reloadKey: filterKey,
  });

  const handleFilterSave = (key: string, selected: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: selected }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const searchableFilter = (title: string, placeholder: string, emptyMessage: string) => ({
    title,
    searchable: true,
    searchPlaceholder: placeholder,
    emptyMessage,
  });

  const headers = [
    {
      label: "Курс",
      key: "course",
      fieldName: "assignment__lesson__course__title",
      width: "15%",
      isFilter: true,
      filterType: "list" as const,
      listOptions: filterOptions.courses,
      listFilter: searchableFilter("Курс", "Найти курс", "Курсы не найдены"),
    },
    {
      label: "Урок",
      key: "lesson",
      fieldName: "assignment__lesson__title",
      width: "15%",
      isFilter: true,
      filterType: "list" as const,
      listOptions: filterOptions.lessons,
      listFilter: searchableFilter("Урок", "Найти урок", "Уроки не найдены"),
    },
    {
      label: "Задание",
      key: "assignment",
      fieldName: "assignment__title",
      width: "20%",
      isFilter: true,
      filterType: "list" as const,
      listOptions: filterOptions.assignments,
      listFilter: searchableFilter("Задание", "Найти задание", "Задания не найдены"),
    },
    {
      label: "ФИО",
      key: "studentName",
      fieldName: "student__full_name",
      width: "12%",
      isFilter: true,
      filterType: "list" as const,
      listOptions: filterOptions.students,
      listFilter: searchableFilter("ФИО", "Найти ученика", "Ученики не найдены"),
    },
    { label: "Статус", key: "status", fieldName: "is_reviewed", width: "9%" },
    { label: "Ответы", key: "assignmentTitle", width: "8%" },
    { label: "Telegram", key: "telegram", fieldName: "student__user_name", width: "10%" },
    { label: "Телефон", key: "phoneNumber", fieldName: "student__phone_number", width: "11%" },
  ];

  const rowAmountOptions = [
    { label: "7 записей", value: 7 },
    { label: "15 записей", value: 15 },
    { label: "20 записей", value: 20 },
  ];

  return (
    <AssignmentResponsesPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>Домашние задания</Title>
          <TableMetrics
            items={[
              { label: "Непроверенных заданий", value: unreviewedCount },
            ]}
          />
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Ответов пока нет",
            description:
              "Здесь появятся работы учеников, отправленные на проверку.",
          }}
          headers={headers}
          rows={assignmentResponses}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onSort={handleSort}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
          minHeight="200px"
          activeFilter={activeFilter}
          handleActiveFilterChange={setActiveFilter}
          appliedFilters={filters}
          onFilterSave={handleFilterSave}
          onFilterReset={handleFilterReset}
        />
      </ContentWrapper>
    </AssignmentResponsesPageContainer>
  );
};

export default AssignmentResponsesPage;
